import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Accordion, Card, Table } from "react-bootstrap";
import { BadgeDomain } from "../../../../_components/Abstractions/BadgeDomain";
import { stringToDateView } from "../../../../domain/Configuration";
import { TransactionTypeBadge } from "../../../../_components/OrderViews/Components/TransactionTypeBadge";
import { StatusBadge } from "../../../../_components/Abstractions/StatusBadge";
import s from "./style.module.css";
import { ContractorPricingOffcanvas } from "../CreateContractor/ContractorRatePlans/ContractorRatePlanForm";
import { ContractorRelatedRatePlansDetails } from "./ContractorRelatedRatePlansDetails";
import { ContractorRatePlansHistory } from "./ContractorRatePlansHistory";
import { DomainButton } from "../../../../_components/Abstractions/DomainButton";
import { Printer } from "react-bootstrap-icons";
import { DeleteContractorRatePlan } from "../CreateContractor/ContractorRatePlans/DeleteContractorRatePlan";
import { useSelector } from "react-redux";
import { USER_ROLES } from "../../../../domain/UserRoles";

export const ContractorRatePlanDetails = ({ eventKey, ratePlan, contractorId, contractorName, contractorTypes, intermediaries, addresses, isRelatedView }) => {
  const intermediariesDictionary = intermediaries ? Object.fromEntries(intermediaries?.map((x) => [x.intermediaryId, x.name])) : [];
  const addressDictionary = addresses ? Object.fromEntries(addresses?.map((x) => [x.id, x.name])) : [];
  const formRef = useRef();
  const handlePrint = useReactToPrint({ content: () => formRef.current });
  const details = useSelector((x) => x.auth.details);
  const { role } = details || {};
  const addressName = addressDictionary[ratePlan.addressId];
  const isAddressDefined = !!addressName || !ratePlan.addressId;
  const isAdmin = role === USER_ROLES.ADMIN || role === USER_ROLES.SUPER_USER;

  return <>
    <Accordion.Item eventKey={eventKey} key={ratePlan.id}>
      <Accordion.Header>
        <Card.Subtitle className="upper">
          Cennik {ratePlan.intermediaryId ?
          <>pośrednika <BadgeDomain bg="success">{intermediariesDictionary[ratePlan.intermediaryId]}</BadgeDomain></> : "klienta"}
          {ratePlan.addressId && <> (adres: {addressName?.toUpperCase()})</>}
          {": "} [od {stringToDateView(ratePlan.dateFrom)} do {stringToDateView(ratePlan.dateTo)}]
          {" "}<TransactionTypeBadge transactionType={ratePlan.type}/>
          {" "}<StatusBadge isActive={isAddressDefined && ratePlan.isActive}/>
        </Card.Subtitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className={`d-flex mb-2 ${s.printHide}`}>
          <div className="d-flex gap-1">
            {isAddressDefined && (<ContractorPricingOffcanvas
              ratePlanId={ratePlan?.id}
              contractorId={contractorId}
              contractorTypes={contractorTypes}
              intermediaries={intermediaries}
              addresses={addresses}
              btnTitle="Edytuj"
              size="smaller"
              btnSize="sm"
              ratePlan={ratePlan}
              isUpdate
            />)}
            {!isRelatedView && isAddressDefined &&
              <>
                <ContractorPricingOffcanvas
                  ratePlanId={null}
                  contractorId={contractorId}
                  contractorTypes={contractorTypes}
                  intermediaries={intermediaries}
                  addresses={addresses}
                  btnTitle="Kopiuj cennik"
                  size="smaller"
                  btnSize="sm"
                  ratePlan={ratePlan}
                  isClone
                />
                <ContractorRelatedRatePlansDetails size="sm" ratePlan={ratePlan} contractorId={contractorId}
                                                   contractorTypes={contractorTypes}
                                                   intermediaries={intermediaries}
                                                   addresses={addresses}/>
              </>}
            <ContractorRatePlansHistory size="smaller" ratePlan={ratePlan}/>
            <DomainButton size="smaller" variant="success" onClick={handlePrint}>Drukuj <Printer/></DomainButton>
            {!isRelatedView && isAdmin && <DeleteContractorRatePlan ratePlanId={ratePlan.id} contractorId={contractorId} />}
          </div>
        </div>
        <div ref={formRef} className={s.pageBreak}>
          <Card.Subtitle className={s.printContent}>
            Cennik {ratePlan.intermediaryId &&
            <>pośrednika <BadgeDomain bg="info">{intermediariesDictionary[ratePlan.intermediaryId]}</BadgeDomain> dla</>} klienta {contractorName}
            {ratePlan.addressId && <> ({addressDictionary[ratePlan.addressId]})</>}
            {" "}<TransactionTypeBadge transactionType={ratePlan.type}/>
          </Card.Subtitle>
          <div className="d-flex my-2 remarks">Uwagi: {ratePlan.remarks ?? "-"}</div>
          <div className="d-flex gap-4">
            <PricingTable
              name="Towary"
              showAdvancedPricing
              items={ratePlan.wastes?.map(wastePrice => <tr key={wastePrice.wasteKind?.wasteKindId ?? wastePrice.wasteId}>
                <td>
                  <BadgeDomain truncateRow bg={wastePrice.isDefined ? (wastePrice.isUsed ? "primary" : "secondary") : "danger"}
                               size="x-small">{wastePrice.waste?.displayDescription} ({wastePrice.wasteKind?.name ?? "-"})</BadgeDomain>
                </td>
                <td>{!wastePrice.isAdvancedPricing ? wastePrice.price?.toFixed(2) : "-"}</td>
                <td>{wastePrice.isAdvancedPricing ? wastePrice.wasteAdvancedPricing?.["Baled"]?.toFixed(2) : "-"}</td>
                <td>{wastePrice.isAdvancedPricing ? wastePrice.wasteAdvancedPricing?.["Loose"]?.toFixed(2) : "-"}</td>
              </tr>)}/>
            <div className="vr"></div>
            <PricingTable
              name="Usługi"
              serviceTable
              showAdvancedPricing
              items={ratePlan.services?.map(serviceRatePlan =>
                <ServicePricingRow
                  key={serviceRatePlan.serviceId}
                  serviceRatePlan={serviceRatePlan}
                  isRelatedView={isRelatedView}
                />)}
            />
          </div>
        </div>
      </Accordion.Body>
      <Card/>
    </Accordion.Item>
  </>;
};

const ServicePricingRow = ({ serviceRatePlan, isRelatedView }) => {
  const getWasteDescription = (pricingItem) => {
    const waste = pricingItem.waste;
    const wasteKind = pricingItem.wasteKindId ? waste.kinds?.find(x => x.wasteKindId === pricingItem.wasteKindId) : null;
    return `${waste?.displayDescription} (${wasteKind?.name ?? "-"})`;
  }

  const items =  serviceRatePlan.serviceWastesAdvancedPricing?.map((item, idx) =>
    <tr key={idx}>
    <td>
      <BadgeDomain truncateRow bg={!item.isPricingMissing ? "primary" : "danger"} size="x-small">{getWasteDescription(item)}</BadgeDomain>
    </td>
    <td>{!serviceRatePlan.isAdvancedPricing ? item?.pricing?.["General"]?.toFixed(2) : "-"}</td>
    <td>{serviceRatePlan.isAdvancedPricing ? item?.pricing?.["Ton"]?.toFixed(2) : "-"}</td>
    <td>{serviceRatePlan.isAdvancedPricing ? item?.pricing?.["Unit"]?.toFixed(2) : "-"}</td>
    <td>{serviceRatePlan.isAdvancedPricing ? item?.pricing?.["RunningMeter"]?.toFixed(2) : "-"}</td>
  </tr>);

  return <>
    <tr>
      <td className="">
        <BadgeDomain truncateRow bg={serviceRatePlan.isDefined ? (serviceRatePlan.isUsed ? "info" : "secondary")  : "danger"} size="x-small">{serviceRatePlan.service?.name}</BadgeDomain>
        {/*<BadgeDomain bg={(serviceRatePlan.isUsed ? "info" : "secondary")} size="x-small">{serviceRatePlan.service?.name}</BadgeDomain>*/}
      </td>
      <td>{!serviceRatePlan.isAdvancedPricing && !serviceRatePlan.isWasteAdvancedPricing ? serviceRatePlan.price?.toFixed(2) ?? "?" : "-"}</td>
      <td>{serviceRatePlan.isAdvancedPricing && !serviceRatePlan.isWasteAdvancedPricing ? serviceRatePlan.serviceAdvancedPricing?.["Ton"]?.toFixed(2) : "-"}</td>
      <td>{serviceRatePlan.isAdvancedPricing && !serviceRatePlan.isWasteAdvancedPricing ? serviceRatePlan.serviceAdvancedPricing?.["Unit"]?.toFixed(2) : "-"}</td>
      <td>{serviceRatePlan.isAdvancedPricing && !serviceRatePlan.isWasteAdvancedPricing ? serviceRatePlan.serviceAdvancedPricing?.["RunningMeter"]?.toFixed(2) : "-"}</td>
    </tr>
    {items}
  </>
}

const PricingTable = ({ name, items, showAdvancedPricing, serviceTable }) => {
  return <div className="w-100">
    {items?.length === 0 && <>{name} - Brak</>}
    {items?.length > 0 && <Table className="mb-3">
      <thead>
      <tr>
        <td style={{ width: "40%" }}>{name}</td>
        <td style={{ width: "14%" }}>Cena {!serviceTable && "/ T"}</td>
        {showAdvancedPricing && !serviceTable && <>
          <td style={{ width: "18%" }}>Cena / T - bel.</td>
          <td style={{ width: "20%" }}>Cena / T - luz</td>
        </>}
        {showAdvancedPricing && serviceTable && <>
          <td style={{ width: "15%" }}>Cena / T</td>
          <td style={{ width: "15%" }}>Cena / szt.</td>
          <td style={{ width: "20%" }}>Cena / mb.</td>
        </>}
      </tr>
      </thead>
      <tbody>
      {items}
      </tbody>
    </Table>}
  </div>;
};